import type { EcwDoctor } from "@/src/lib/ehr/ecw/webecw/endpoints/doctors";
import type { PatientCoreParams } from "@/src/lib/ehr/ecw/webecw/endpoints/set-patient";
import {
  type AutonomyChartEnrichmentOutput,
  AutonomyChartEnrichmentOutcome,
} from "@/src/lib/workflows/steps/step-outputs";
import type { Referral } from "@/src/models/documents/referral";
import { getPersonName } from "@/src/models/person";

const AUTONOMY_MISSING_PHONE = "999-999-9999";
const NPI_PATTERN = /^\d{10}$/;

export type ResolveAutonomyReferralChartEnrichmentInput = {
  created: boolean | null;
  referral: Pick<Referral, "referringProvider" | "receivingProvider">;
  referringProviderId: string | null;
  doctors: readonly EcwDoctor[];
  encounterDoctorId: string | null;
};

function nonblank(value: string | null | undefined): string | null {
  const trimmed = value?.trim();

  return trimmed ? trimmed : null;
}

function usableNpi(value: string | null | undefined): string | null {
  const npi = nonblank(value);

  return npi && NPI_PATTERN.test(npi) ? npi : null;
}

function resolveRenderingProviderId(
  receivingProvider: Referral["receivingProvider"],
  doctors: readonly EcwDoctor[]
): string | null {
  const npi = usableNpi(receivingProvider?.npiNumber);

  if (!npi) return null;

  const ids = new Set(
    doctors
      .filter((doctor) => usableNpi(doctor.NPI) === npi)
      .map((doctor) => nonblank(doctor.id))
      .filter((id): id is string => id !== null)
  );

  return ids.size === 1 ? (ids.values().next().value ?? null) : null;
}

function resolveRenderingMismatch(
  receivingProvider: Referral["receivingProvider"],
  doctors: readonly EcwDoctor[],
  encounterDoctorId: string | null
): AutonomyChartEnrichmentOutput["renderingMismatch"] {
  const existingDoctorId = nonblank(encounterDoctorId);

  if (!receivingProvider || !existingDoctorId) return null;

  const matchingDoctors = doctors.filter((doctor) => nonblank(doctor.id) === existingDoctorId);

  if (matchingDoctors.length !== 1) return null;

  const existingDoctor = matchingDoctors[0];
  const existingNpi = usableNpi(existingDoctor.NPI);
  const referralNpi = usableNpi(receivingProvider.npiNumber);

  if (!existingNpi || !referralNpi || existingNpi === referralNpi) return null;

  const existingProviderName = nonblank(existingDoctor.fullname);
  const referralProviderName = nonblank(getPersonName(receivingProvider));

  if (!existingProviderName || !referralProviderName) return null;

  return {
    existingDoctorId,
    existingProviderName,
    referralProviderName,
  };
}

export function resolveAutonomyReferralChartEnrichment({
  created,
  referral,
  referringProviderId,
  doctors,
  encounterDoctorId,
}: ResolveAutonomyReferralChartEnrichmentInput): AutonomyChartEnrichmentOutput {
  if (created === null) {
    return {
      outcome: AutonomyChartEnrichmentOutcome.ACTION_REQUIRED,
      renderingMismatch: null,
      referringProviderId: null,
      renderingProviderId: null,
    };
  }

  if (!created) {
    return {
      outcome: AutonomyChartEnrichmentOutcome.APPLIED_OR_NOT_NEEDED,
      renderingMismatch: resolveRenderingMismatch(referral.receivingProvider, doctors, encounterDoctorId),
      referringProviderId: null,
      renderingProviderId: null,
    };
  }

  const shouldResolveReferringProvider = referral.referringProvider !== null;
  const shouldResolveRenderingProvider = referral.receivingProvider !== null;
  const resolvedReferringProviderId = usableNpi(referral.referringProvider?.npiNumber)
    ? nonblank(referringProviderId)
    : null;
  const resolvedRenderingProviderId = resolveRenderingProviderId(referral.receivingProvider, doctors);
  const requiresAction =
    (shouldResolveReferringProvider && resolvedReferringProviderId === null) ||
    (shouldResolveRenderingProvider && resolvedRenderingProviderId === null);

  return {
    outcome: requiresAction
      ? AutonomyChartEnrichmentOutcome.ACTION_REQUIRED
      : AutonomyChartEnrichmentOutcome.APPLIED_OR_NOT_NEEDED,
    renderingMismatch: null,
    referringProviderId: shouldResolveReferringProvider ? resolvedReferringProviderId : null,
    renderingProviderId: shouldResolveRenderingProvider ? resolvedRenderingProviderId : null,
  };
}

export function formatAutonomyRenderingMismatch(
  mismatch: NonNullable<AutonomyChartEnrichmentOutput["renderingMismatch"]>
): string {
  return `patient is established with ${mismatch.existingProviderName} (existing rendering provider); referral documents are addressed to ${mismatch.referralProviderName} (provider on the referral).`;
}

export function transformAutonomyReferralCreateCore(core: PatientCoreParams): PatientCoreParams {
  if (nonblank(core.phone) || nonblank(core.mobile)) return core;

  return { ...core, phone: AUTONOMY_MISSING_PHONE };
}
